import {
  ButtonType,
  ButtonWidget
} from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { Messages } from '../../../../codegen/Localize';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { SpaceType } from '../../../state/SpaceInfo';
import { ExplorerFlyoutPanel, ExplorerFlyoutPanelProps } from '../../Components/ExplorerFlyoutPanel';
import { ExplorerStyles } from '../ExplorerStyles';

export interface CreateContentButtonInfo {
  disabled: boolean;
  icon: string;
  title: string | JSX.Element;
  contentType: ExploreContentApi_ContentType;
}

export interface CreateContentButtonStateProps {
  createContentButtons: CreateContentButtonInfo[];
}

export interface CreateContentButtonDispatchProps {
  requestCreateContentButtonsInfo: () => void;
  createContent: (contentType: ExploreContentApi_ContentType, projectId: string) => void;
}

export interface CreateContentButtonOwnProps {
  spaceType: SpaceType;
  flyoutWidth?: number;
  projectId?: string;
}

export type CreateContentButtonProps = CreateContentButtonStateProps & CreateContentButtonDispatchProps & CreateContentButtonOwnProps;

export interface CreateContentButtonState {
  showingCreateButtonsPanel: boolean;
}

export class CreateContentButton extends React.PureComponent<CreateContentButtonProps, CreateContentButtonState> {

  private createButtonElement: HTMLElement;

  public constructor(props: CreateContentButtonProps) {
    super(props);

    this.state = { showingCreateButtonsPanel: false };
  }

  private setCreateButton = (component: ButtonWidget): void => {
    this.createButtonElement = ReactDOM.findDOMNode(component) as HTMLElement;
  }

  private showCreateButtonsPanel = (): void => {
    this.props.requestCreateContentButtonsInfo();

    this.setState({showingCreateButtonsPanel: !this.state.showingCreateButtonsPanel});
  }

  private hideCreateButtons = (): void => {
    this.setState({showingCreateButtonsPanel: false});
  }

  private onCreateContentClick = (contentType: ExploreContentApi_ContentType): void => {
    this.props.createContent(contentType, this.props.projectId);
  }

  private createButtonDisabled = (): boolean => {
    return this.props.createContentButtons.length === 0;
  }

  private shouldRenderCreateButtons = (): boolean => {
    return this.state.showingCreateButtonsPanel && !this.createButtonDisabled();
  }

  private renderCreateButtonsPanel = (): JSX.Element => {
    const panelProps: ExplorerFlyoutPanelProps = {
      anchorEl: this.createButtonElement,
      testId: CreateButtonContentTestIds.CreateButtonsPanel,
      onCloseRequest: this.hideCreateButtons,
      title: Messages.createContentMenuTitle(),
      width: this.props.flyoutWidth || 150
    };

    return (
      <ExplorerFlyoutPanel {...panelProps}>
        {this.props.createContentButtons.map((button) => {
          return (
            <div
              data-tb-test-id={'create-' + button.contentType + '-button'}
              className={button.disabled ? ExplorerStyles.CreateButtonContainerDisabled : ExplorerStyles.CreateButtonContainerEnabled}
              key={button.contentType}
              onClick={() => {
                if (button.disabled) {
                  return;
                }

                this.hideCreateButtons();
                this.onCreateContentClick(button.contentType);
              }
              }>
                <img
                  src={button.icon}
                  className={button.disabled ? ExplorerStyles.CreateButtonImgDisabled : ExplorerStyles.CreateButtonImg}
                />
                {button.title}
            </div>
          );
        })}
      </ExplorerFlyoutPanel>
    );
  }

  public render(): JSX.Element {
    return <div>
        <ButtonWidget buttonType={ButtonType.Outline}
          children={Messages.create()}
          handleClick={this.showCreateButtonsPanel}
          testId={CreateButtonContentTestIds.Button}
          disabled={this.createButtonDisabled()}
          ref={this.setCreateButton}/>
        {this.shouldRenderCreateButtons() && this.renderCreateButtonsPanel()}
    </div>;
  }
}

export namespace CreateButtonContentTestIds {
  export const Button = 'explorer-create-content';
  export const CreateButtonsPanel = 'create-content-buttons-panel';
}
